import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Edit3, Eye, EyeOff, Loader2, Zap, Bell, Star, Flame, Gift, Megaphone, Monitor, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const ICON_MAP = { rocket: "🚀", calendar: "📅", star: "⭐", zap: "⚡", megaphone: "📢", gift: "🎁", alert: "🔔", fire: "🔥", sparkle: "✨", trophy: "🏆" };
const ICON_OPTIONS = [{ value: "", label: "None" }, ...Object.entries(ICON_MAP).map(([k, v]) => ({ value: k, label: `${v} ${k}` }))];
const FREQ_OPTIONS = [
  { value: "once_session", label: "Once per session" },
  { value: "once_day", label: "Once per day" },
  { value: "every_visit", label: "Every page visit" },
];
const TYPE_OPTIONS = [
  { value: "cinematic", label: "Cinematic (Full-screen overlay)" },
  { value: "bubble", label: "Bubble (Bottom-right expandable)" },
];

export default function AdminPopups() {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/popups/admin/list`, getAuth());
      setPopups(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this popup?")) return;
    await axios.delete(`${API}/popups/admin/${id}`, getAuth());
    fetchData();
  };
  const handleToggle = async (item) => {
    await axios.put(`${API}/popups/admin/${item.popup_id}`, { is_active: !item.is_active }, getAuth());
    fetchData();
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading...</div>;

  return (
    <div data-testid="admin-popups">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Popup Messages</h1>
          <p className="text-sm text-gray-500 font-dm-sans mt-1">Cinematic overlays & floating bubbles on the landing page</p>
        </div>
        <button data-testid="add-popup-btn" onClick={() => { setEditItem(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-dm-sans font-bold hover:bg-gray-800 transition-colors rounded-md">
          <Plus size={16} /> Add Popup
        </button>
      </div>

      {popups.length === 0 && <div className="text-center py-12 text-gray-400 font-dm-sans">No popups created yet.</div>}

      <div className="space-y-3">
        {popups.map(item => (
          <div key={item.popup_id} className={`bg-white border rounded-lg px-5 py-4 flex items-start gap-4 ${item.is_active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${item.popup_type === "cinematic" ? "bg-purple-50 text-purple-600" : "bg-cyan-50 text-cyan-600"}`}>
              {item.popup_type === "cinematic" ? <Monitor size={18} /> : <MessageCircle size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-dm-sans font-bold text-gray-900">{item.icon && ICON_MAP[item.icon] ? `${ICON_MAP[item.icon]} ` : ""}{item.title}</p>
                <span className={`text-[10px] font-dm-sans font-bold px-2 py-0.5 rounded-full ${item.popup_type === "cinematic" ? "bg-purple-50 text-purple-600" : "bg-cyan-50 text-cyan-600"}`}>
                  {item.popup_type === "cinematic" ? "Cinematic" : "Bubble"}
                </span>
                <span className={`text-[10px] font-dm-sans font-bold px-2 py-0.5 rounded-full ${item.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                  {item.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <p className="text-xs text-gray-500 font-dm-sans mt-0.5 line-clamp-2">{item.message}</p>
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400 font-dm-sans">
                <span>Freq: {FREQ_OPTIONS.find(f => f.value === item.frequency)?.label || item.frequency}</span>
                <span>Priority: {item.priority}</span>
                {item.start_date && <span>From: {item.start_date.slice(0, 10)}</span>}
                {item.end_date && <span>Until: {item.end_date.slice(0, 10)}</span>}
                {item.cta_text && <span>CTA: {item.cta_text}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => handleToggle(item)} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid={`toggle-popup-${item.popup_id}`}>
                {item.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
              <button onClick={() => { setEditItem(item); setShowForm(true); }} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid={`edit-popup-${item.popup_id}`}>
                <Edit3 size={15} />
              </button>
              <button onClick={() => handleDelete(item.popup_id)} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`delete-popup-${item.popup_id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <PopupFormDialog open={showForm} onClose={() => { setShowForm(false); setEditItem(null); }} item={editItem} onSaved={fetchData} />
    </div>
  );
}

function PopupFormDialog({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({
    title: "", message: "", popup_type: "cinematic", icon: "", image_url: "",
    cta_text: "", cta_url: "", frequency: "once_session", start_date: "", end_date: "", priority: 10, is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        title: item.title || "", message: item.message || "", popup_type: item.popup_type || "cinematic",
        icon: item.icon || "", image_url: item.image_url || "", cta_text: item.cta_text || "",
        cta_url: item.cta_url || "", frequency: item.frequency || "once_session",
        start_date: item.start_date?.slice(0, 16) || "", end_date: item.end_date?.slice(0, 16) || "",
        priority: item.priority || 10, is_active: item.is_active !== false
      });
    } else {
      setForm({ title: "", message: "", popup_type: "cinematic", icon: "", image_url: "", cta_text: "", cta_url: "", frequency: "once_session", start_date: "", end_date: "", priority: 10, is_active: true });
    }
  }, [item]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.message.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.start_date) payload.start_date = new Date(payload.start_date).toISOString();
      if (payload.end_date) payload.end_date = new Date(payload.end_date).toISOString();
      if (item) {
        await axios.put(`${API}/popups/admin/${item.popup_id}`, payload, getAuth());
      } else {
        await axios.post(`${API}/popups/admin/create`, payload, getAuth());
      }
      onSaved();
      onClose();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-gray-900">{item ? "Edit Popup" : "Create Popup"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Popup Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {TYPE_OPTIONS.map(t => (
                <button key={t.value} type="button" onClick={() => u("popup_type", t.value)}
                  className={`py-2.5 text-xs font-dm-sans font-bold rounded-md border transition-colors ${form.popup_type === t.value ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
                  {t.value === "cinematic" ? <Monitor size={14} className="inline mr-1.5" /> : <MessageCircle size={14} className="inline mr-1.5" />}
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Title *</label>
            <input data-testid="popup-title-input" value={form.title} onChange={e => u("title", e.target.value)}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md" placeholder="e.g. New AI Masterclass!" />
          </div>
          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Message *</label>
            <textarea data-testid="popup-message-input" value={form.message} onChange={e => u("message", e.target.value)}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md h-20 resize-none" placeholder="Your popup message..." />
          </div>
          {/* Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Icon</label>
            <div className="flex flex-wrap gap-1.5">
              {ICON_OPTIONS.map(o => (
                <button key={o.value} type="button" onClick={() => u("icon", o.value)}
                  className={`px-2.5 py-1 text-xs font-dm-sans rounded-md border transition-colors ${form.icon === o.value ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-200"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          {/* Image URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Image URL <span className="text-gray-400">(optional)</span></label>
            <input value={form.image_url} onChange={e => u("image_url", e.target.value)}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md" placeholder="https://..." />
          </div>
          {/* CTA */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">CTA Button Text</label>
              <input value={form.cta_text} onChange={e => u("cta_text", e.target.value)}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md" placeholder="Learn More" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">CTA Link</label>
              <input value={form.cta_url} onChange={e => u("cta_url", e.target.value)}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md" placeholder="/ai-tools" />
            </div>
          </div>
          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Display Frequency</label>
            <select value={form.frequency} onChange={e => u("frequency", e.target.value)}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md bg-white">
              {FREQ_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          {/* Schedule */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Start Date</label>
              <input type="datetime-local" value={form.start_date} onChange={e => u("start_date", e.target.value)}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">End Date</label>
              <input type="datetime-local" value={form.end_date} onChange={e => u("end_date", e.target.value)}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md" />
            </div>
          </div>
          {/* Priority + Active */}
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Priority</label>
              <input type="number" min="1" value={form.priority} onChange={e => u("priority", parseInt(e.target.value) || 1)}
                className="w-20 border border-gray-300 focus:border-black px-3 py-2 text-sm font-dm-sans outline-none rounded-md" />
            </div>
            <label className="flex items-center gap-2 mt-5 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => u("is_active", e.target.checked)} className="rounded" />
              <span className="text-sm font-dm-sans text-gray-700">Active</span>
            </label>
          </div>
          <button data-testid="save-popup-btn" onClick={handleSave} disabled={saving}
            className="w-full bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60 rounded-md">
            {saving ? "Saving..." : (item ? "Update Popup" : "Create Popup")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
